import { createApi } from '@reduxjs/toolkit/query/react';
import type { FetchArgs, FetchBaseQueryError } from '@reduxjs/toolkit/query';

import { baseQueryWithReauth, fetchAllPages } from 'utils/services';

import { Cart, Product, ProductCategory, StoreRecurringWorkflow } from 'models/shop';

import {
  AddProductToCartReq,
  CancelPrescriptionOrderRes,
  CancelRecurringWorkflowReq,
  CompletePrescriptionIntakeReq,
  CompletePrescriptionIntakeRes,
  CheckoutReqProps,
  CheckoutResProps,
  GetCartInfoRes,
  GetCheckoutAttemptRes,
  GetProductCategoriesRes,
  GetProductDetailsRes,
  GetProductsListReq,
  GetProductsListRes,
  GetQuizSessionStatusRes,
  GetRecurringWorkflowsRes,
  GetShopOrdersRes,
  GetSingleOrderDetailsRes,
  LaunchQuizRequestReq,
  LaunchQuizRequestRes,
  PauseRecurringWorkflowReq
} from './shop.types';

export const shopApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: (build) => ({
    addToCart: build.mutation<unknown, AddProductToCartReq>({
      invalidatesTags: (_result, error) => (error ? [] : ['cart']),
      query: ({ productId, ...body }) => ({
        body,
        method: 'POST',
        url: `/store/cart/${productId}`
      })
    }),
    cancelPrescriptionOrder: build.mutation<CancelPrescriptionOrderRes, string>({
      invalidatesTags: ['shop'],
      query: (id) => ({
        method: 'POST',
        url: `/store/orders/${id}/cancel`
      })
    }),
    cancelRecurringWorkflow: build.mutation<unknown, CancelRecurringWorkflowReq>({
      invalidatesTags: ['RecurringWorkflow'],
      query: ({ workflowId }) => ({
        method: 'POST',
        url: `/store/recurring/${workflowId}/cancel`
      })
    }),
    completePrescriptionIntake: build.mutation<
      CompletePrescriptionIntakeRes,
      CompletePrescriptionIntakeReq
    >({
      invalidatesTags: ['shop'],
      query: ({ medicalIntakeFormId, orderId, productId }) => ({
        body: { medicalIntakeFormId },
        method: 'POST',
        url: `/store/orders/${orderId}/intakes/${productId}/complete`
      })
    }),
    deleteFromCart: build.mutation<unknown, Pick<AddProductToCartReq, 'productId'>>({
      invalidatesTags: (_result, error) => (error ? [] : ['cart']),
      query: ({ productId }) => ({
        method: 'DELETE',
        url: `/store/cart/${productId}`
      })
    }),
    getCart: build.query<Cart, void>({
      keepUnusedDataFor: 0,
      providesTags: ['cart'],
      query: () => ({
        url: '/store/cart'
      }),
      transformResponse: (response: GetCartInfoRes) => response.data
    }),
    getCheckoutAttempt: build.query<GetCheckoutAttemptRes, string>({
      query: (checkoutAttemptId) => ({
        url: `/store/checkout-attempts/${checkoutAttemptId}`
      })
    }),
    getProductCategories: build.query<ProductCategory[], void>({
      query: () => ({
        url: '/store/product-categories'
      }),
      transformResponse: (response: GetProductCategoriesRes) => response.data
    }),
    getProducts: build.query<GetProductsListRes, GetProductsListReq>({
      keepUnusedDataFor: 0,
      async queryFn(arg, api, extraOptions, baseQuery) {
        const {
          fetchAllPages: shouldFetchAllPages = true,
          limit = 20,
          pageNo = 0,
          ...restParams
        } = arg ?? {};

        if (!shouldFetchAllPages) {
          const request: FetchArgs = {
            params: { ...restParams, limit, pageNo },
            url: '/store/products'
          };

          const result = await baseQuery(request);

          if (result.error) {
            return { error: result.error as FetchBaseQueryError };
          }

          return { data: result.data as GetProductsListRes };
        }

        return fetchAllPages<Product, GetProductsListRes>({
          api,
          baseQuery: baseQuery as typeof baseQueryWithReauth,
          extraOptions,
          limit,
          pageNo,
          params: restParams,
          url: '/store/products'
        });
      }
    }),
    getQuizSessionStatus: build.query<GetQuizSessionStatusRes, string>({
      query: (lineItemId) => ({
        url: `/store/quizzes/${lineItemId}`
      })
    }),
    getRecurringWorkflows: build.query<StoreRecurringWorkflow[], void>({
      providesTags: ['RecurringWorkflow'],
      query: () => ({
        url: '/store/recurring'
      }),
      transformResponse: (response: GetRecurringWorkflowsRes) => response.data.workflows
    }),
    getShopOrders: build.query<GetShopOrdersRes, void>({
      providesTags: ['shop'],
      query: () => ({
        url: '/store/orders'
      })
    }),
    getSingleOrderDetails: build.query<GetSingleOrderDetailsRes, string>({
      providesTags: ['shop'],
      query: (id) => ({
        url: `/store/orders/${id}`
      })
    }),
    getSingleProductDetails: build.query<GetProductDetailsRes, string>({
      query: (id) => ({
        url: `/store/products/${id}`
      })
    }),
    launchQuizRequest: build.mutation<LaunchQuizRequestRes, LaunchQuizRequestReq>({
      invalidatesTags: (_result, error) => (error ? [] : ['cart']),
      query: ({ lineItemId, ...body }) => ({
        body,
        method: 'POST',
        url: `/store/quizzes/${lineItemId}/launch`
      })
    }),
    pauseRecurringWorkflow: build.mutation<unknown, PauseRecurringWorkflowReq>({
      invalidatesTags: ['RecurringWorkflow'],
      query: ({ resumeAt, workflowId }) => ({
        body: { resumeAt },
        method: 'POST',
        url: `/store/recurring/${workflowId}/pause`
      })
    }),
    storeCheckout: build.mutation<CheckoutResProps, CheckoutReqProps>({
      // Only a paid checkout empties the server-side cart. The `processPayment: false`
      // calls are order previews, so they must leave the cached cart alone.
      invalidatesTags: (_result, error, arg) => (!error && arg.processPayment ? ['cart'] : []),
      query: (body) => ({
        body,
        method: 'POST',
        url: '/store/checkout'
      })
    })
  }),
  reducerPath: 'shopApi',
  tagTypes: ['cart', 'shop', 'RecurringWorkflow']
});

export const {
  useAddToCartMutation,
  useCancelPrescriptionOrderMutation,
  useCancelRecurringWorkflowMutation,
  useCompletePrescriptionIntakeMutation,
  useDeleteFromCartMutation,
  useGetCartQuery,
  useGetProductCategoriesQuery,
  useGetProductsQuery,
  useGetQuizSessionStatusQuery,
  useGetRecurringWorkflowsQuery,
  useGetShopOrdersQuery,
  useGetSingleOrderDetailsQuery,
  useGetSingleProductDetailsQuery,
  useLaunchQuizRequestMutation,
  useLazyGetCartQuery,
  useLazyGetCheckoutAttemptQuery,
  useLazyGetQuizSessionStatusQuery,
  useLazyGetShopOrdersQuery,
  useLazyGetSingleOrderDetailsQuery,
  usePauseRecurringWorkflowMutation,
  useStoreCheckoutMutation
} = shopApi;
